import React, { Suspense, useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { FadeIn, StaggeredEntrance } from '@atlaskit/motion';
import { Box, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import { HoverCard } from '../hover-card/HoverCard';

import { messages } from './messages';

const styles = cssMap({
	wrapper: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minHeight: '0px',
		height: '100%',
		width: '100%',
		overflowX: 'hidden',
	},
	browseItemsContainer: {
		overflow: 'hidden',
		flex: 1,
	},
	browseItemsList: {
		overflowY: 'auto',
		scrollbarGutter: 'stable',
		paddingLeft: token('space.250'),
		paddingRight: token('space.075'),
		paddingTop: token('space.100'),
		listStyle: 'none',
		marginTop: token('space.0'),
	},
	listFooterSpinner: {
		display: 'flex',
		justifyContent: 'center',
		paddingBlock: token('space.100'),
	},
	browseItemButton: {
		width: '100%',
		height: '56px',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		backgroundColor: token('color.background.neutral.subtle'),
		textAlign: 'left',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			borderRadius: token('radius.small'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	browseItemInner: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		width: '100%',
	},
	browseItemIconContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		color: token('color.text.subtle'),
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.medium'),
		border: `${token('border.width')} solid ${token('color.border')}`,
	},
	descriptionExpandable: {
		display: 'grid',
		overflow: 'hidden',
		transition: `grid-template-rows 100ms cubic-bezier(0.2, 0, 0, 1)`,
	},
	descriptionExpandableInner: {
		minHeight: '0px',
		overflow: 'hidden',
	},
});

const isSafariBrowser = () =>
	typeof navigator !== 'undefined' && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export interface BrowseItem {
	id: string;
	name: string;
	description?: string;
	iconName?: string;
	iconColor?: string;
	product?: string;
	appIds?: string[];
	roles?: string[];
}

export type InlineBrowseIconProps = { label?: string; color?: string };

interface BrowseItemIconProps {
	iconName?: string;
	iconColor?: string;
	iconMap?: Record<string, React.ComponentType<InlineBrowseIconProps>>;
	defaultIcon?: React.ComponentType<InlineBrowseIconProps>;
}

const BrowseItemIcon = ({ iconName, iconColor, iconMap, defaultIcon }: BrowseItemIconProps) => {
	const IconComponent =
		iconName && iconMap && iconName in iconMap ? iconMap[iconName] : defaultIcon;

	if (!IconComponent) {
		return null;
	}

	return (
		<Suspense fallback={<Spinner size="small" />}>
			<IconComponent label="" {...(iconColor && { color: iconColor })} />
		</Suspense>
	);
};
interface BrowseItemProps {
	browseItem: BrowseItem;
	onClick: () => void;
	onHover?: (browseItem: BrowseItem | null) => void;
	testId?: string;
	iconMap?: Record<string, React.ComponentType<InlineBrowseIconProps>>;
	defaultIcon?: React.ComponentType<InlineBrowseIconProps>;
	renderHoverCardContent?: (browseItem: BrowseItem) => React.ReactNode;
}

const BrowseItem = ({
	browseItem,
	onClick,
	onHover,
	testId,
	iconMap,
	defaultIcon,
	renderHoverCardContent,
}: BrowseItemProps) => {
	const [isHovered, setIsHovered] = useState(false);

	const handleMouseEnter = useCallback(() => {
		setIsHovered(true);
		onHover?.(browseItem);
	}, [browseItem, onHover]);

	const handleMouseLeave = useCallback(() => {
		setIsHovered(false);
		onHover?.(null);
	}, [onHover]);

	const shouldRenderInlineDescription = Boolean(browseItem.description) && !isSafariBrowser();

	const innerContent = (
		<Box xcss={styles.browseItemInner}>
			<Box xcss={styles.browseItemIconContainer}>
				<BrowseItemIcon
					iconName={browseItem.iconName}
					iconColor={browseItem.iconColor}
					iconMap={iconMap}
					defaultIcon={defaultIcon}
				/>
			</Box>
			<Stack space="space.025">
				<Text>{browseItem.name}</Text>
				{shouldRenderInlineDescription && (
					<Box
						xcss={styles.descriptionExpandable}
						style={{ gridTemplateRows: isHovered ? '1fr' : '0fr' }}
					>
						<Box xcss={styles.descriptionExpandableInner}>
							<Text size="small" color="color.text.subtlest" maxLines={1}>
								{browseItem.description}
							</Text>
						</Box>
					</Box>
				)}
			</Stack>
		</Box>
	);

	return (
		<Pressable
			xcss={styles.browseItemButton}
			onClick={onClick}
			testId={testId}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onFocus={handleMouseEnter}
			onBlur={handleMouseLeave}
		>
			{renderHoverCardContent ? (
				<HoverCard
					content={renderHoverCardContent(browseItem)}
					placement="right"
					fallbackPlacements={['left', 'bottom', 'top']}
					testId={testId ? `${testId}--hovercard` : undefined}
				>
					{innerContent}
				</HoverCard>
			) : (
				innerContent
			)}
		</Pressable>
	);
};

/* eslint-disable-next-line @atlaskit/platform/one-value-export-per-file -- legacy onboarding baseline */
export const filterBrowseItems = (browseItems: BrowseItem[], searchValue: string): BrowseItem[] => {
	if (!searchValue) {
		return browseItems;
	}
	const search = searchValue.toLowerCase();
	return browseItems.filter(
		(browseItem) =>
			browseItem.name.toLowerCase().includes(search) ||
			browseItem.description?.toLowerCase().includes(search) ||
			browseItem.product?.toLowerCase().includes(search) ||
			browseItem.appIds?.some((appId) => appId.toLowerCase().includes(search)) ||
			browseItem.roles?.some((role) => role.toLowerCase().includes(search)),
	);
};

export interface InlineBrowseProps {
	browseItems?: BrowseItem[];
	onSelectBrowseItem: (browseItem: BrowseItem) => void;
	onBrowseItemHover?: (browseItem: BrowseItem | null) => void;
	onClose?: () => void;
	iconMap?: Record<string, React.ComponentType<InlineBrowseIconProps>>;
	defaultIcon?: React.ComponentType<InlineBrowseIconProps>;
	renderHoverCardContent?: (browseItem: BrowseItem) => React.ReactNode;
	listEndRef?: (element: Element | null) => void;
	listFooterLoading?: boolean;
}

/* eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports, @atlaskit/platform/one-value-export-per-file -- legacy onboarding baseline */
export const InlineBrowse = ({
	browseItems = [],
	onSelectBrowseItem,
	onBrowseItemHover,
	onClose,
	iconMap,
	defaultIcon,
	renderHoverCardContent,
	listEndRef,
	listFooterLoading,
}: InlineBrowseProps): React.ReactNode => {
	const { formatMessage } = useIntl();

	const handleBrowseItemSelect = useCallback(
		(browseItem: BrowseItem) => {
			onSelectBrowseItem(browseItem);
			onClose?.();
		},
		[onSelectBrowseItem, onClose],
	);

	return (
		<Box xcss={styles.wrapper}>
			<Stack space="space.150" xcss={styles.browseItemsContainer}>
				<Stack xcss={styles.browseItemsList} as="ul">
					{browseItems.length === 0 && listFooterLoading && (
						<Box as="li" xcss={styles.listFooterSpinner} paddingBlockStart="space.200">
							<Spinner size="medium" />
						</Box>
					)}
					{browseItems.length === 0 && !listFooterLoading && (
						<Box paddingBlockStart="space.200" as="li">
							<Text align="center" color="color.text.subtle">
								{formatMessage(messages.noResults)}
							</Text>
						</Box>
					)}
					{browseItems.length > 0 && (
						<StaggeredEntrance>
							{browseItems.map((browseItem) => (
								<FadeIn key={browseItem.id}>
									{(props) => (
										<li {...props}>
											<BrowseItem
												browseItem={browseItem}
												onClick={() => handleBrowseItemSelect(browseItem)}
												onHover={onBrowseItemHover}
												testId={`browse-item-${browseItem.id}`}
												iconMap={iconMap}
												defaultIcon={defaultIcon}
												renderHoverCardContent={renderHoverCardContent}
											/>
										</li>
									)}
								</FadeIn>
							))}
						</StaggeredEntrance>
					)}
					{listFooterLoading && browseItems.length > 0 && (
						<Box as="li" xcss={styles.listFooterSpinner}>
							<Spinner size="medium" />
						</Box>
					)}
					{listEndRef && browseItems.length > 0 && (
						<Box as="li" paddingBlockStart="space.0">
							<div ref={(el) => listEndRef(el)} />
						</Box>
					)}
				</Stack>
			</Stack>
		</Box>
	);
};
